import stylesheet from './StoryCard.scss?inline';

import CustomComponent from "../../base/customComponent/CustomComponent";

/**
 * `nxt-story-card` — A standalone story card with optional link navigation.
 *
 * Unlike `nxt-success-story-card`, this component has no overlay. Clicking the
 * card navigates to the URL provided via the `href` attribute.
 *
 * ## Attributes
 * | Attribute  | Type   | Default                       | Description                              |
 * |------------|--------|-------------------------------|------------------------------------------|
 * | `href`     | string | —                             | URL the card links to. When omitted the body renders as a non-interactive `<div>`. |
 * | `target`   | string | `_self`                       | Forwarded to the `<a>` element.          |
 * | `backdrop` | string | `backdrop-opacity-black-high` | CSS class applied for the image overlay. |
 *
 * ## Slots
 * | Slot name    | Description                                    |
 * |--------------|------------------------------------------------|
 * | `tag`        | Optional tag/label rendered in the top corner. |
 * | `background` | Background image element.                      |
 * | `title`      | Card heading.                                  |
 * | *(default)*  | Short descriptive text (2-line clamp). An arrow icon is rendered alongside it. |
 *
 * @example
 * ```html
 * <nxt-story-card href="/stories/example" target="_blank">
 *   <nxt-tag slot="tag" color="transparent" left-dot="#F97316">Industry</nxt-tag>
 *   <img slot="background" src="..." alt="" />
 *   <span slot="title">Project Title</span>
 *   Short description of the story.
 * </nxt-story-card>
 * ```
 */
class NXTStoryCard extends CustomComponent {
    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get backdrop(): string {
        return this.getAttribute('backdrop') ?? 'backdrop-opacity-black-high';
    }

    get href(): string | undefined {
        return this.getAttribute('href') ?? undefined;
    }

    get target(): string {
        return this.getAttribute('target') ?? '_self';
    }

    render() {
        const card = document.createElement('div');
        card.classList.add('story-card', this.backdrop);

        const bodyTag = this.href ? 'a' : 'div';
        const body = document.createElement(bodyTag);
        body.classList.add('body');
        if (this.href) {
            (body as HTMLAnchorElement).href = this.href;
            (body as HTMLAnchorElement).target = this.target;
        }

        body.innerHTML = `
            <div class="top">
                <slot name="tag"></slot>
            </div>
            <div class="bottom">
                <div class="title"></div>
                <div class="subtitle">
                    <div class="text"></div>
                    <span class="arrow"></span>
                </div>
            </div>
        `;

        card.innerHTML = `<div class="background"></div>`;
        card.appendChild(body);

        this.cloneSlotContentIntoContainer(body.querySelector('.title'), 'title');
        this.cloneSlotContentIntoContainer(card.querySelector('.background'), 'background');
        this.preventImgDraggable(card.querySelector('.background'));
        this.cloneDefaultSlotContentIntoContainer(body.querySelector('.text'));
        return card;
    }

    preventImgDraggable(container: HTMLElement) {
        container.querySelectorAll('img').forEach(img => {
            img.setAttribute('draggable', 'false');
        });
    }
}

customElements.define('nxt-story-card', NXTStoryCard);
